export type CodeFormatLanguage = "javascript" | "typescript" | "json" | "markup";

export async function formatCodeForDisplay(source: string, language: CodeFormatLanguage) {
  if (!source.trim()) return "";
  if (language === "json") return formatJson(source);
  if (language !== "javascript" && language !== "typescript") return source;

  try {
    return await formatJavaScriptLike(source, language);
  } catch {
    return source;
  }
}

async function formatJavaScriptLike(source: string, language: "javascript" | "typescript") {
  const [prettier, estree, languagePlugin] = await Promise.all([
    import("prettier/standalone"),
    import("prettier/plugins/estree"),
    language === "typescript"
      ? import("prettier/plugins/typescript")
      : import("prettier/plugins/babel"),
  ]);
  const options = {
    parser: language === "typescript" ? "typescript" : "babel",
    plugins: [languagePlugin, estree],
    printWidth: 72,
    tabWidth: 2,
    semi: true,
  };

  try {
    const formatted = await prettier.format(source, options);
    return formatted.trimEnd();
  } catch (err) {
    if (!/^\s*return\b/.test(source)) throw err;
    const wrapped = `function __workflow_step__() {\n${source}\n}`;
    const formattedWrapped = await prettier.format(wrapped, options);
    return unwrapFunctionBody(formattedWrapped) ?? source;
  }
}

function unwrapFunctionBody(source: string) {
  const lines = source.trimEnd().split("\n");
  if (lines.length < 3 || !lines[0]?.startsWith("function __workflow_step__")) return null;
  if (lines.at(-1) !== "}") return null;
  return lines
    .slice(1, -1)
    .map((line) => (line.startsWith("  ") ? line.slice(2) : line))
    .join("\n");
}

function formatJson(source: string) {
  try {
    return JSON.stringify(JSON.parse(source), null, 2);
  } catch {
    return source;
  }
}
